/**
 * A complete, pure plan for a custom repair.
 *
 * The browser and an agent both need to build the same estimate, orders and
 * expenses. This module decides what those rows contain and what they total;
 * callers resolve reference data first and persist the returned rows later.
 * No generated id or document number belongs in a plan.
 */

import { isRepairTrade } from "./contract.js";
import type { InsuredCrew, TradeList } from "./insurance.js";
import {
  computeLadder,
  contractBaseForMargin,
  type LadderInput,
  type LadderTotals,
} from "./ladder.js";
import {
  addCents,
  cents,
  multiplyCents,
  percentOf,
  type Cents,
  type Percent,
  toDecimal,
} from "./money.js";
import {
  DEFAULT_JOB_OVERHEAD_PCT,
  DEFAULT_MATERIAL_TAX_RATE,
  DEFAULT_WORK_TAX_RATE,
  priceFromCents,
} from "./pricing.js";
import type { PaymentType } from "./types.js";

export const REPAIR_ESTIMATE_REF = "estimate" as const;
export const REPAIR_SCOPE_LINE_REF = "scope-line" as const;

export interface RepairProductReference {
  id: string;
  name: string;
  unit?: string | null;
  defaultCostCents: Cents;
  active?: boolean | null;
}

export interface RepairSupplierReference {
  id: string;
  price_list_id?: string | null;
  active?: boolean | null;
}

export interface RepairProductPriceReference {
  price_list_id: string;
  product_id: string;
  costCents: Cents;
}

export interface RepairCrewReference extends InsuredCrew {
  id: string;
  active?: boolean | null;
}

export interface RepairReferenceData {
  validRepairTrades: readonly string[];
  products: readonly RepairProductReference[];
  suppliers: readonly RepairSupplierReference[];
  productPrices: readonly RepairProductPriceReference[];
  crews: readonly RepairCrewReference[];
  defaults?: {
    jobOverheadPct?: number;
    materialTaxRate?: number;
    workTaxRate?: number;
    autoExpenseOverrides?: Readonly<Record<string, Cents>> | null;
    autoExpenseRemovals?: Readonly<Record<string, boolean>> | null;
  };
}

export interface RepairOrderLineInput {
  productId?: string | null | undefined;
  /** Required for a free-text line; overrides the catalogue name otherwise. */
  description?: string | null | undefined;
  quantity: number;
  unit?: string | null | undefined;
  /** Omitted uses catalogue cost. Explicit zero remains zero. */
  unitCostCents?: Cents | undefined;
}

export interface RepairMaterialOrderInput {
  supplierId: string;
  taxRate?: number | undefined;
  lines: readonly RepairOrderLineInput[];
}

export interface RepairWorkOrderInput {
  crewId: string;
  taxRate?: number | undefined;
  startAt?: string | null | undefined;
  description?: string | null | undefined;
  trades?: TradeList;
  lines: readonly RepairOrderLineInput[];
}

export interface RepairExpenseInput {
  name: string;
  amountCents: Cents;
  paymentType: PaymentType;
}

export interface PlanRepairInput {
  /** The caller's current Central date, fixed for this plan and its ladder. */
  asOfDate: string;
  job: { id: string; jobType?: string | null };
  version: number;
  createdByProfileId?: string | null | undefined;
  heading?: string | null | undefined;
  trade: string;
  scope: string;
  /** Protective clauses selected by the scope-drafting service. */
  scopeQualifiers?: readonly string[] | undefined;
  /** Omission means unpriced. Zero is an explicit customer price. */
  customerPriceCents?: Cents;
  /** Used only when customerPriceCents is omitted. Fifty means a 50% margin. */
  targetMarginPct?: number;
  materialOrders?: readonly RepairMaterialOrderInput[] | undefined;
  workOrder?: RepairWorkOrderInput | null | undefined;
  expenses?: readonly RepairExpenseInput[] | undefined;
  references: RepairReferenceData;
}

export type RepairValidationCode =
  | "missing_scope"
  | "invalid_trade"
  | "empty_order_lines"
  | "unknown_reference"
  | "missing_required_cost"
  | "invalid_quantity"
  | "invalid_money"
  | "invalid_rate"
  | "invalid_name"
  | "invalid_date"
  | "invalid_version";

export interface RepairValidationFailure {
  code: RepairValidationCode;
  path: string;
  message: string;
}

export interface PlannedRepairEstimateRow {
  ref: typeof REPAIR_ESTIMATE_REF;
  job_id: string;
  name: string;
  version: number;
  status: "draft";
  trade: string;
  roof_type: null;
  warranty: "none";
  key_differentiator: null;
  job_overhead_pct: number;
  created_by: string | null;
}

export interface PlannedRepairScopeLineRow {
  ref: typeof REPAIR_SCOPE_LINE_REF;
  estimateRef: typeof REPAIR_ESTIMATE_REF;
  product_id: null;
  trade: string;
  description: string;
  note: string;
  scope_qualifiers: readonly string[];
  qty: 1;
  unit: "EA";
  customerPriceCents: Cents;
  discountCents: Cents;
  work_doing: true;
  customer_visible: true;
  sort_order: 10;
}

export interface PlannedRepairOrderLine {
  parentRef: string;
  product_id: string | null;
  description: string;
  qty: number;
  unit: string;
  unitCostCents: Cents;
  lineCostCents: Cents;
  sort_order: number;
}

export interface PlannedRepairMaterialOrder {
  ref: string;
  estimateRef: typeof REPAIR_ESTIMATE_REF;
  job_id: string;
  supplier_id: string;
  trade: string;
  status: "not_placed";
  taxRate: Percent;
  reconcileCents: Cents;
  created_by: string | null;
  lines: readonly PlannedRepairOrderLine[];
  subtotalCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
}

export interface PlannedRepairWorkOrder {
  ref: "work-order";
  estimateRef: typeof REPAIR_ESTIMATE_REF;
  job_id: string;
  crew_id: string;
  trades: readonly string[];
  status: "not_scheduled";
  start_at: string | null;
  description: string | null;
  taxRate: Percent;
  reconcileCents: Cents;
  created_by: string | null;
  lines: readonly PlannedRepairOrderLine[];
  subtotalCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
}

export interface PlannedRepairExpense {
  ref: string;
  estimateRef: typeof REPAIR_ESTIMATE_REF;
  job_id: string;
  trade: string;
  name: string;
  amountCents: Cents;
  payment_type: PaymentType;
  sort_order: number;
  created_by: string | null;
}

export interface RepairSuggestedPrice {
  customerPriceCents: Cents;
  /** The user-supplied direct costs divided by 0.6. */
  basis: {
    materialCents: Cents;
    laborCents: Cents;
    expenseCents: Cents;
    totalCents: Cents;
  };
  /** Its real result after the complete ladder, not a promised 40% margin. */
  ladder: LadderTotals;
}

export interface RepairPlan {
  estimate: PlannedRepairEstimateRow;
  scopeLine: PlannedRepairScopeLineRow;
  pricing: {
    status: "priced" | "unpriced";
    source: "explicit" | "target_margin" | "unpriced";
    targetMarginPct: number | null;
    suggested: RepairSuggestedPrice | null;
  };
  materialOrders: readonly PlannedRepairMaterialOrder[];
  workOrder: PlannedRepairWorkOrder | null;
  expenses: readonly PlannedRepairExpense[];
  ladder: LadderTotals;
}

export type PlanRepairResult =
  | { ok: true; plan: RepairPlan }
  | { ok: false; errors: readonly RepairValidationFailure[] };

const PAYMENT_TYPES: readonly PaymentType[] = [
  "Cash",
  "CreditCard",
  "Check",
  "ACH",
];

function strictCents(value: unknown): Cents | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? cents(value)
    : null;
}

function validRate(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 100
  );
}

function cleanTrades(value: TradeList, fallback: string): string[] {
  const raw = typeof value === "string" ? value.split(",") : (value ?? []);
  const trades = raw.map((one) => (one ?? "").trim()).filter(Boolean);
  return trades.length > 0 ? trades : [fallback];
}

/** Build a complete repair plan without reading or writing anything. */
export function planRepair(input: PlanRepairInput): PlanRepairResult {
  const errors: RepairValidationFailure[] = [];
  const fail = (code: RepairValidationCode, path: string, message: string) =>
    errors.push({ code, path, message });

  const scope = input.scope.trim();
  const date = new Date(`${input.asOfDate}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(input.asOfDate) ||
    Number.isNaN(date.getTime()) ||
    date.toISOString().slice(0, 10) !== input.asOfDate
  ) {
    fail("invalid_date", "asOfDate", "A plan needs a valid ISO date.");
  }
  if (!scope) fail("missing_scope", "scope", "A repair needs a scope of work.");

  const trade = input.trade.trim();
  if (
    !isRepairTrade(trade) ||
    !input.references.validRepairTrades.includes(trade)
  ) {
    fail("invalid_trade", "trade", "Choose one of the resolved repair trades.");
  }

  if (!Number.isInteger(input.version) || input.version < 1) {
    fail(
      "invalid_version",
      "version",
      "Estimate version must be a positive integer.",
    );
  }

  const heading = input.heading?.trim() || "Repair";
  const overhead =
    input.references.defaults?.jobOverheadPct ?? DEFAULT_JOB_OVERHEAD_PCT;
  if (!validRate(overhead)) {
    fail(
      "invalid_rate",
      "references.defaults.jobOverheadPct",
      "Job overhead must be a non-negative finite rate.",
    );
  }

  const hasCustomerPrice = input.customerPriceCents !== undefined;
  let customerPrice = hasCustomerPrice
    ? strictCents(input.customerPriceCents)
    : (0 as Cents);
  if (hasCustomerPrice && customerPrice == null) {
    fail(
      "invalid_money",
      "customerPriceCents",
      "Customer price must be non-negative finite cents.",
    );
  }
  const hasTargetMargin =
    !hasCustomerPrice && input.targetMarginPct !== undefined;
  if (
    hasTargetMargin &&
    (typeof input.targetMarginPct !== "number" ||
      !Number.isFinite(input.targetMarginPct) ||
      input.targetMarginPct < 0 ||
      input.targetMarginPct >= 100)
  ) {
    fail(
      "invalid_rate",
      "targetMarginPct",
      "Target margin must be at least zero and less than 100 percent.",
    );
  }
  const scopeQualifiers = [...new Set(input.scopeQualifiers ?? [])];
  if (
    scopeQualifiers.length > 12 ||
    scopeQualifiers.some((id) => typeof id !== "string" || !id.trim())
  ) {
    fail(
      "unknown_reference",
      "scopeQualifiers",
      "Protective clauses must be non-empty resolved references, up to twelve.",
    );
  }

  const products = new Map(
    input.references.products.map((row) => [row.id, row]),
  );
  const suppliers = new Map(
    input.references.suppliers.map((row) => [row.id, row]),
  );
  const crews = new Map(input.references.crews.map((row) => [row.id, row]));
  const listedCosts = new Map<string, Cents>();
  for (const price of input.references.productPrices) {
    const cost = strictCents(price.costCents);
    if (cost != null) {
      listedCosts.set(`${price.price_list_id}:${price.product_id}`, cost);
    }
  }

  const planLines = (
    lines: readonly RepairOrderLineInput[],
    path: string,
    parentRef: string,
    supplier: RepairSupplierReference | null,
  ): PlannedRepairOrderLine[] => {
    if (lines.length === 0) {
      fail(
        "empty_order_lines",
        `${path}.lines`,
        "An order must contain at least one line.",
      );
      return [];
    }
    return lines.map((line, index) => {
      const linePath = `${path}.lines[${index}]`;
      const product = line.productId ? products.get(line.productId) : null;
      if (line.productId && (!product || product.active === false)) {
        fail(
          "unknown_reference",
          `${linePath}.productId`,
          "The selected product is unavailable.",
        );
      }
      const description =
        line.description?.trim() || product?.name.trim() || "";
      if (!description) {
        fail(
          "invalid_name",
          `${linePath}.description`,
          "A free-text line needs a description.",
        );
      }
      if (!Number.isFinite(line.quantity) || line.quantity <= 0) {
        fail(
          "invalid_quantity",
          `${linePath}.quantity`,
          "Quantity must be greater than zero.",
        );
      }

      let unitCost: Cents | null = null;
      if (line.unitCostCents !== undefined) {
        unitCost = strictCents(line.unitCostCents);
        if (unitCost == null) {
          fail(
            "invalid_money",
            `${linePath}.unitCostCents`,
            "Unit cost must be non-negative finite cents.",
          );
        }
      } else if (product) {
        const list = supplier?.price_list_id;
        unitCost = list
          ? (listedCosts.get(`${list}:${product.id}`) ?? null)
          : null;
        unitCost ??= strictCents(product.defaultCostCents);
        if (unitCost == null) {
          fail(
            "missing_required_cost",
            `${linePath}.unitCostCents`,
            "The line has no explicit, supplier, or catalogue cost.",
          );
        }
      } else {
        fail(
          "missing_required_cost",
          `${linePath}.unitCostCents`,
          "A free-text line needs an explicit unit cost.",
        );
      }

      const safeCost = unitCost ?? (0 as Cents);
      const safeQuantity =
        Number.isFinite(line.quantity) && line.quantity > 0 ? line.quantity : 0;
      const lineCost = multiplyCents(safeCost, safeQuantity);
      if (!Number.isSafeInteger(lineCost)) {
        fail(
          "invalid_money",
          `${linePath}.quantity`,
          "Quantity and unit cost produce a line total outside exact cents.",
        );
      }
      return {
        parentRef,
        product_id: product?.id ?? null,
        description,
        qty: safeQuantity,
        unit: line.unit?.trim() || product?.unit?.trim() || "EA",
        unitCostCents: safeCost,
        lineCostCents: lineCost,
        sort_order: (index + 1) * 10,
      };
    });
  };

  const materialOrders = (input.materialOrders ?? []).map((order, index) => {
    const path = `materialOrders[${index}]`;
    const supplier = suppliers.get(order.supplierId);
    if (!supplier || supplier.active === false) {
      fail(
        "unknown_reference",
        `${path}.supplierId`,
        "The selected supplier is unavailable.",
      );
    }
    const rate =
      order.taxRate ??
      input.references.defaults?.materialTaxRate ??
      DEFAULT_MATERIAL_TAX_RATE;
    if (!validRate(rate)) {
      fail(
        "invalid_rate",
        `${path}.taxRate`,
        "Material tax must be a non-negative finite rate.",
      );
    }
    const ref = `material-order:${index + 1}`;
    const lines = planLines(order.lines, path, ref, supplier ?? null);
    const subtotal = addCents(...lines.map((line) => line.lineCostCents));
    const tax = percentOf(subtotal, (validRate(rate) ? rate : 0) as Percent);
    if (
      !Number.isSafeInteger(subtotal) ||
      !Number.isSafeInteger(tax) ||
      !Number.isSafeInteger(addCents(subtotal, tax))
    ) {
      fail("invalid_money", path, "Order totals exceed exact cents.");
    }
    return {
      ref,
      estimateRef: REPAIR_ESTIMATE_REF,
      job_id: input.job.id,
      supplier_id: order.supplierId,
      trade,
      status: "not_placed" as const,
      taxRate: (validRate(rate) ? rate : 0) as Percent,
      reconcileCents: 0 as Cents,
      created_by: input.createdByProfileId ?? null,
      lines,
      subtotalCents: subtotal,
      taxCents: tax,
      totalCents: addCents(subtotal, tax),
    };
  });

  let workOrder: PlannedRepairWorkOrder | null = null;
  let workOrderCrew: RepairCrewReference | null = null;
  if (input.workOrder) {
    const order = input.workOrder;
    workOrderCrew = crews.get(order.crewId) ?? null;
    if (!workOrderCrew || workOrderCrew.active === false) {
      fail(
        "unknown_reference",
        "workOrder.crewId",
        "The selected crew is unavailable.",
      );
    }
    const rate =
      order.taxRate ??
      input.references.defaults?.workTaxRate ??
      DEFAULT_WORK_TAX_RATE;
    if (!validRate(rate)) {
      fail(
        "invalid_rate",
        "workOrder.taxRate",
        "Work-order tax must be a non-negative finite rate.",
      );
    }
    const lines = planLines(order.lines, "workOrder", "work-order", null);
    const subtotal = addCents(...lines.map((line) => line.lineCostCents));
    const tax = percentOf(subtotal, (validRate(rate) ? rate : 0) as Percent);
    if (
      !Number.isSafeInteger(subtotal) ||
      !Number.isSafeInteger(tax) ||
      !Number.isSafeInteger(addCents(subtotal, tax))
    ) {
      fail("invalid_money", "workOrder", "Order totals exceed exact cents.");
    }
    workOrder = {
      ref: "work-order",
      estimateRef: REPAIR_ESTIMATE_REF,
      job_id: input.job.id,
      crew_id: order.crewId,
      trades: cleanTrades(order.trades, trade),
      status: "not_scheduled",
      start_at: order.startAt ?? null,
      description: order.description?.trim() || null,
      taxRate: (validRate(rate) ? rate : 0) as Percent,
      reconcileCents: 0 as Cents,
      created_by: input.createdByProfileId ?? null,
      lines,
      subtotalCents: subtotal,
      taxCents: tax,
      totalCents: addCents(subtotal, tax),
    };
  }

  const expenses = (input.expenses ?? []).map((expense, index) => {
    const name = expense.name.trim();
    if (!name)
      fail(
        "invalid_name",
        `expenses[${index}].name`,
        "An expense needs a name.",
      );
    const amount = strictCents(expense.amountCents);
    if (amount == null) {
      fail(
        "invalid_money",
        `expenses[${index}].amountCents`,
        "Expense amount must be non-negative finite cents.",
      );
    }
    if (!PAYMENT_TYPES.includes(expense.paymentType)) {
      fail(
        "unknown_reference",
        `expenses[${index}].paymentType`,
        "Choose a valid payment type.",
      );
    }
    return {
      ref: `expense:${index + 1}`,
      estimateRef: REPAIR_ESTIMATE_REF,
      job_id: input.job.id,
      trade,
      name,
      amountCents: amount ?? (0 as Cents),
      payment_type: expense.paymentType,
      sort_order: (index + 1) * 10,
      created_by: input.createdByProfileId ?? null,
    };
  });

  const combinedCost = addCents(
    ...materialOrders.map((order) => order.totalCents),
    workOrder?.totalCents ?? (0 as Cents),
    ...expenses.map((expense) => expense.amountCents),
  );
  if (hasTargetMargin && combinedCost === 0) {
    fail(
      "missing_required_cost",
      "targetMarginPct",
      "A target margin needs a material, labor, or expense cost. Supply an explicit price or leave this repair unpriced.",
    );
  }
  if (
    !Number.isSafeInteger(combinedCost) ||
    (!hasCustomerPrice &&
      combinedCost > 0 &&
      !Number.isSafeInteger(priceFromCents(combinedCost)))
  ) {
    fail(
      "invalid_money",
      "ordersAndExpenses",
      "Repair costs exceed exact cents.",
    );
  }
  if (errors.length > 0) return { ok: false, errors };

  const estimate: PlannedRepairEstimateRow = {
    ref: REPAIR_ESTIMATE_REF,
    job_id: input.job.id,
    name: heading,
    version: input.version,
    status: "draft",
    trade,
    roof_type: null,
    warranty: "none",
    key_differentiator: null,
    job_overhead_pct: overhead,
    created_by: input.createdByProfileId ?? null,
  };
  const scopeLine: PlannedRepairScopeLineRow = {
    ref: REPAIR_SCOPE_LINE_REF,
    estimateRef: REPAIR_ESTIMATE_REF,
    product_id: null,
    trade,
    description: heading,
    note: scope,
    scope_qualifiers: scopeQualifiers,
    qty: 1,
    unit: "EA",
    customerPriceCents: customerPrice ?? (0 as Cents),
    discountCents: 0 as Cents,
    work_doing: true,
    customer_visible: true,
    sort_order: 10,
  };

  const ladderInput = (price: Cents): LadderInput => ({
    asOfDate: input.asOfDate,
    estimate: {
      office_discount: 0,
      job_overhead_pct: overhead,
      trade,
      roof_type: null,
      warranty: "none",
    },
    jobType: input.job.jobType,
    layouts: [],
    autoExpenseOverrides: input.references.defaults?.autoExpenseOverrides,
    autoExpenseRemovals: input.references.defaults?.autoExpenseRemovals,
    items: [
      {
        customer_price: toDecimal(price),
        discount: 0,
        is_upgrade: false,
        work_doing: true,
      },
    ],
    changeOrders: [],
    materialOrders: materialOrders.map((order) => ({
      tax_rate: order.taxRate,
      reconcile_amt: 0,
      items: order.lines.map((line) => ({
        qty: line.qty,
        unit_price: toDecimal(line.unitCostCents),
      })),
    })),
    workOrders: workOrder
      ? [
          {
            tax_rate: workOrder.taxRate,
            reconcile_amt: 0,
            crew_id: workOrder.crew_id,
            trades: workOrder.trades,
            items: workOrder.lines.map((line) => ({
              qty: line.qty,
              unit_price: toDecimal(line.unitCostCents),
            })),
          },
        ]
      : [],
    workOrderCrews: workOrder ? [workOrderCrew] : [],
    expenses: expenses.map((expense) => ({
      amount: toDecimal(expense.amountCents),
    })),
  });
  const ladderFor = (price: Cents): LadderTotals =>
    computeLadder(ladderInput(price));

  if (hasTargetMargin && errors.length === 0) {
    customerPrice = contractBaseForMargin(
      ladderInput(0 as Cents),
      input.targetMarginPct! / 100,
    );
    scopeLine.customerPriceCents = customerPrice;
    if (!Number.isSafeInteger(customerPrice)) {
      fail(
        "invalid_money",
        "targetMarginPct",
        "The target margin produces a price outside exact cents.",
      );
    }
  }
  if (errors.length > 0) return { ok: false, errors };

  const materialCents = addCents(
    ...materialOrders.map((order) => order.totalCents),
  );
  const laborCents = workOrder?.totalCents ?? (0 as Cents);
  const expenseCents = addCents(
    ...expenses.map((expense) => expense.amountCents),
  );
  const basisTotal = addCents(materialCents, laborCents, expenseCents);
  const suggestedPrice =
    !hasCustomerPrice && !hasTargetMargin && basisTotal > 0
      ? priceFromCents(basisTotal)
      : null;

  return {
    ok: true,
    plan: {
      estimate,
      scopeLine,
      pricing: {
        status: hasCustomerPrice || hasTargetMargin ? "priced" : "unpriced",
        source: hasCustomerPrice
          ? "explicit"
          : hasTargetMargin
            ? "target_margin"
            : "unpriced",
        targetMarginPct: hasTargetMargin ? input.targetMarginPct! : null,
        suggested:
          suggestedPrice == null
            ? null
            : {
                customerPriceCents: suggestedPrice,
                basis: {
                  materialCents,
                  laborCents,
                  expenseCents,
                  totalCents: basisTotal,
                },
                ladder: ladderFor(suggestedPrice),
              },
      },
      materialOrders,
      workOrder,
      expenses,
      ladder: ladderFor(scopeLine.customerPriceCents),
    },
  };
}
